import { useEffect, useState, useCallback } from "react";
import { api, API_BASE } from "../lib/api";
import {
  Clock, Cpu, Brain, Mail, BookOpen, ExternalLink, Sparkles,
  Key, Eye, EyeOff, CheckCircle, Trash2, Save, Plus, X, Bell, ToggleLeft, ToggleRight,
} from "lucide-react";
import { toast } from "sonner";

// ─── helpers ────────────────────────────────────────────────────────────────
const pad2 = (n) => String(n).padStart(2, "0");

/** Convert UTC hh:mm to local hh:mm label */
function utcToLocalLabel(hour, minute) {
  const now = new Date();
  const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute));
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function Settings() {
  const [accounts, setAccounts] = useState([]);
  const [hints, setHints] = useState([]);

  // AI key state
  const [aiKey, setAiKey] = useState({ has_key: false, masked: null });
  const [keyInput, setKeyInput] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [keySaving, setKeySaving] = useState(false);
  const [keyDeleting, setKeyDeleting] = useState(false);

  // Digest schedule state
  const [slots, setSlots] = useState([]);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleLoaded, setScheduleLoaded] = useState(false);

  const docsUrl = `${API_BASE}/docs`;

  // ── load ──────────────────────────────────────────────────────────────────
  const loadSchedule = useCallback(async () => {
    try {
      const r = await api.get("/settings/digest-schedule");
      setSlots(r.data.slots || []);
      setScheduleLoaded(true);
    } catch { setScheduleLoaded(true); }
  }, []);

  useEffect(() => {
    api.get("/email-accounts").then((r) => setAccounts(r.data)).catch(() => {});
    api.get("/ai/learning").then((r) => setHints(r.data.hints || [])).catch(() => {});
    api.get("/settings/ai-key").then((r) => setAiKey(r.data)).catch(() => {});
    loadSchedule();
  }, [loadSchedule]);

  // ── AI key handlers ────────────────────────────────────────────────────────
  const handleSaveKey = async () => {
    if (!keyInput.trim()) return;
    setKeySaving(true);
    try {
      const r = await api.put("/settings/ai-key", { api_key: keyInput.trim() });
      setAiKey({ has_key: true, masked: r.data.masked });
      setKeyInput("");
      toast.success("Clé API OpenAI sauvegardée !");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la sauvegarde.");
    } finally { setKeySaving(false); }
  };

  const handleDeleteKey = async () => {
    if (!window.confirm("Supprimer la clé API OpenAI ? L'IA ne pourra plus classifier les emails.")) return;
    setKeyDeleting(true);
    try {
      await api.delete("/settings/ai-key");
      setAiKey({ has_key: false, masked: null });
      setKeyInput("");
      toast.success("Clé API supprimée.");
    } catch { toast.error("Erreur lors de la suppression."); }
    finally { setKeyDeleting(false); }
  };

  // ── Digest schedule handlers ───────────────────────────────────────────────
  const addSlot = () => {
    if (slots.length >= 5) { toast.error("Maximum 5 créneaux."); return; }
    setSlots([...slots, { hour: 8, minute: 0, enabled: true }]);
  };

  const removeSlot = (idx) => setSlots(slots.filter((_, i) => i !== idx));

  const updateSlot = (idx, field, value) => {
    setSlots(slots.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const saveSchedule = async () => {
    setScheduleSaving(true);
    try {
      await api.put("/settings/digest-schedule", { slots });
      toast.success("Horaires sauvegardés !");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la sauvegarde.");
    } finally { setScheduleSaving(false); }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 md:p-8" data-testid="settings-page">
      <div className="mb-8">
        <div className="overline mb-1">// Configuration</div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>
          Paramètres
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* ── OpenAI API Key ────────────────────────────────────────────── */}
        <div className="bg-white border-2 border-[#002FA7] p-6 md:col-span-2">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200">
            <div className="w-9 h-9 bg-[#002FA7] flex items-center justify-center">
              <Key className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: "'Work Sans', sans-serif" }}>
                Clé API OpenAI
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Requise pour la classification IA et les résumés automatiques
              </p>
            </div>
            {aiKey.has_key && (
              <span className="ml-auto inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold">
                <CheckCircle className="w-3.5 h-3.5" /> Configurée
              </span>
            )}
          </div>

          {aiKey.has_key && (
            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 px-4 py-3 mb-4">
              <div>
                <div className="text-xs text-gray-500 mb-0.5">Clé active</div>
                <code className="text-sm font-mono text-gray-800">{aiKey.masked}</code>
              </div>
              <button
                onClick={handleDeleteKey}
                disabled={keyDeleting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {keyDeleting ? "Suppression…" : "Supprimer"}
              </button>
            </div>
          )}

          <div className="space-y-3">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider">
              {aiKey.has_key ? "Remplacer la clé" : "Entrer votre clé API"}
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  id="openai-api-key-input"
                  type={showKey ? "text" : "password"}
                  value={keyInput}
                  onChange={(e) => setKeyInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSaveKey()}
                  placeholder="sk-proj-…"
                  className="w-full px-4 py-2.5 pr-10 border border-gray-300 text-sm font-mono focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <button
                id="save-openai-key-btn"
                onClick={handleSaveKey}
                disabled={keySaving || !keyInput.trim()}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#002FA7] text-white text-sm font-medium hover:bg-[#0038CC] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Save className="w-4 h-4" />
                {keySaving ? "Sauvegarde…" : "Sauvegarder"}
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Votre clé est chiffrée (AES-128 + HMAC-SHA256) avant d'être stockée.{" "}
              <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer"
                className="text-[#002FA7] hover:underline">
                Obtenir une clé sur platform.openai.com →
              </a>
            </p>
          </div>
        </div>

        {/* ── Digest Schedule ────────────────────────────────────────────── */}
        <div className="bg-white border-2 border-gray-200 hover:border-gray-300 transition-colors p-6 md:col-span-2">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200">
            <div className="w-9 h-9 bg-gray-900 flex items-center justify-center">
              <Bell className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold" style={{ fontFamily: "'Work Sans', sans-serif" }}>
                Horaires des résumés automatiques
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                L'IA génère et envoie un briefing email à chaque créneau actif (heure UTC). Max 5 créneaux.
              </p>
            </div>
            <button
              id="save-schedule-btn"
              onClick={saveSchedule}
              disabled={scheduleSaving || !scheduleLoaded}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
            >
              <Save className="w-4 h-4" />
              {scheduleSaving ? "Sauvegarde…" : "Sauvegarder"}
            </button>
          </div>

          {!scheduleLoaded ? (
            <div className="text-sm text-gray-400 animate-pulse">Chargement…</div>
          ) : (
            <div className="space-y-3">
              {slots.length === 0 && (
                <div className="text-sm text-gray-500 py-4 text-center border border-dashed border-gray-200">
                  Aucun créneau configuré. Cliquez sur "Ajouter un créneau" pour commencer.
                </div>
              )}

              {slots.map((slot, idx) => (
                <div
                  key={idx}
                  className={`flex items-center gap-3 p-3 border transition-colors ${
                    slot.enabled ? "border-gray-200 bg-white" : "border-gray-100 bg-gray-50 opacity-60"
                  }`}
                >
                  {/* Toggle enabled */}
                  <button
                    id={`slot-toggle-${idx}`}
                    onClick={() => updateSlot(idx, "enabled", !slot.enabled)}
                    className="text-gray-400 hover:text-gray-700 transition-colors shrink-0"
                    title={slot.enabled ? "Désactiver ce créneau" : "Activer ce créneau"}
                  >
                    {slot.enabled
                      ? <ToggleRight className="w-6 h-6 text-[#002FA7]" />
                      : <ToggleLeft className="w-6 h-6" />}
                  </button>

                  {/* Hour selector */}
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <select
                      id={`slot-hour-${idx}`}
                      value={slot.hour}
                      onChange={(e) => updateSlot(idx, "hour", parseInt(e.target.value))}
                      className="border border-gray-300 text-sm px-2 py-1.5 font-mono focus:outline-none focus:border-[#002FA7] bg-white"
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>{pad2(h)}</option>
                      ))}
                    </select>
                    <span className="text-gray-400 font-bold">:</span>
                    <select
                      id={`slot-minute-${idx}`}
                      value={slot.minute}
                      onChange={(e) => updateSlot(idx, "minute", parseInt(e.target.value))}
                      className="border border-gray-300 text-sm px-2 py-1.5 font-mono focus:outline-none focus:border-[#002FA7] bg-white"
                    >
                      {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((mn) => (
                        <option key={mn} value={mn}>{pad2(mn)}</option>
                      ))}
                    </select>
                    <span className="text-xs text-gray-400 ml-1">UTC</span>
                  </div>

                  {/* Local time hint */}
                  <div className="flex-1 text-xs text-gray-500">
                    ≈{" "}
                    <span className="font-semibold text-gray-700">
                      {utcToLocalLabel(slot.hour, slot.minute)}
                    </span>{" "}
                    heure locale
                  </div>

                  {/* Status badge */}
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 border ${
                    slot.enabled
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-gray-100 text-gray-500 border-gray-200"
                  }`}>
                    {slot.enabled ? "Actif" : "Inactif"}
                  </span>

                  {/* Remove button */}
                  <button
                    id={`slot-remove-${idx}`}
                    onClick={() => removeSlot(idx)}
                    className="text-gray-300 hover:text-red-500 transition-colors shrink-0"
                    title="Supprimer ce créneau"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}

              {slots.length < 5 && (
                <button
                  id="add-slot-btn"
                  onClick={addSlot}
                  className="flex items-center gap-2 px-4 py-2.5 border border-dashed border-gray-300 text-sm text-gray-600 hover:border-gray-500 hover:text-gray-900 transition-colors w-full justify-center"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter un créneau
                </button>
              )}

              <p className="text-xs text-gray-400 pt-1">
                Les créneaux sont en <strong>UTC</strong>. L'heure locale affichée est calculée automatiquement selon votre navigateur.
                Par défaut (sans configuration) : 09h00 et 15h00 UTC.
              </p>
            </div>
          )}
        </div>

        {/* ── AI Model ──────────────────────────────────────────────────── */}
        <Card title="Modèle IA" icon={Brain}>
          <Field label="Provider">OpenAI</Field>
          <Field label="Modèle">GPT-4o</Field>
          <Field label="Usage">Classification + résumés + auto-apprentissage</Field>
          <Field label="Catégories">
            important • urgent • administratif • commercial • newsletter • promotion • spam • système
          </Field>
        </Card>

        {/* ── Email accounts ────────────────────────────────────────────── */}
        <Card title={`Comptes email (${accounts.length})`} icon={Mail}>
          {accounts.length === 0 ? (
            <div className="text-sm text-gray-500">Aucun compte configuré.</div>
          ) : (
            <ul className="space-y-2 text-sm max-h-48 overflow-y-auto">
              {accounts.map((a) => (
                <li key={a.id} className="border-b border-gray-100 pb-2">
                  <div className="font-medium text-gray-900">{a.email}</div>
                  <div className="text-xs text-gray-500 font-mono">
                    {a.imap_host}:{a.imap_port} · {a.smtp_host}:{a.smtp_port}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* ── Security ──────────────────────────────────────────────────── */}
        <Card title="Sécurité" icon={Cpu}>
          <Field label="Auth">JWT (Bearer, expiration 7j)</Field>
          <Field label="Mot de passe">bcrypt</Field>
          <Field label="Identifiants IMAP/SMTP">Chiffrement Fernet (AES-128 + HMAC-SHA256)</Field>
          <Field label="Clé API OpenAI">Chiffrement Fernet (AES-128 + HMAC-SHA256)</Field>
          <Field label="Webhooks">Signature HMAC-SHA256 (X-SmartMail-Signature)</Field>
        </Card>

        {/* ── AI Learning ───────────────────────────────────────────────── */}
        <Card title={`IA — Auto-apprentissage (${hints.length})`} icon={Sparkles}>
          {hints.length === 0 ? (
            <div className="text-sm text-gray-500">
              Aucune correction enregistrée. Ouvrez un email et cliquez sur la bonne catégorie pour entraîner l'IA.
            </div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="learning-hints-list">
              {hints.slice(0, 20).map((h, i) => (
                <div key={i} className="flex items-center justify-between text-xs border-b border-gray-100 pb-1.5">
                  <code className="text-gray-700 truncate">{h.from_email}</code>
                  <span className="px-2 py-0.5 bg-blue-50 text-[#002FA7] border border-blue-200 uppercase tracking-wider text-[10px] font-semibold ml-2">
                    {h.category}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* ── API Docs ──────────────────────────────────────────────────── */}
        <Card title="API REST documentée" icon={BookOpen}>
          <Field label="OpenAPI 3.1">
            <a href={docsUrl} target="_blank" rel="noreferrer"
              data-testid="api-docs-link"
              className="inline-flex items-center gap-1.5 text-[#002FA7] hover:underline font-medium">
              Swagger UI <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <span className="text-gray-500 text-xs ml-2">({docsUrl})</span>
          </Field>
          <Field label="Schéma JSON">
            <a href={`${API_BASE}/openapi.json`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-[#002FA7] hover:underline font-medium">
              openapi.json <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </Field>
          <Field label="Authentification">
            Bearer JWT — header <code className="bg-gray-100 px-1 py-0.5 text-xs">Authorization: Bearer &lt;token&gt;</code>
          </Field>
        </Card>

      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────
function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
        <div className="w-9 h-9 bg-gray-50 border border-gray-200 flex items-center justify-center">
          <Icon className="w-4 h-4 text-gray-700" />
        </div>
        <h3 className="text-lg font-semibold" style={{ fontFamily: "'Work Sans', sans-serif" }}>{title}</h3>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="border-b border-gray-100 pb-2">
      <div className="overline">{label}</div>
      <div className="text-sm text-gray-800 mt-1">{children}</div>
    </div>
  );
}
